import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import PriceLevelsEditor from './PriceLevelsEditor';

const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'JPY', symbol: '¥' },
  { code: 'AUD', symbol: 'A$' },
  { code: 'CAD', symbol: 'C$' },
];

export default function InstrumentModal({ isOpen, onClose, onSave, initialData }) {
  const [formData, setFormData] = useState({ 
    code: '', 
    name: '', 
    notes: '', 
    currency: 'USD',
    priceLevels: [] 
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(initialData || { 
        code: '', 
        name: '', 
        notes: '', 
        currency: 'USD',
        priceLevels: [] 
      });
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(initialData ? { ...formData, id: initialData.id } : { ...formData, id: uuidv4() });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-app-surface border border-app-border rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-6 border-b border-app-border">
          <h2 className="text-xl font-black uppercase tracking-widest text-white">
            {initialData ? 'Edit Asset' : 'Add New Asset'}
          </h2>
          <button onClick={onClose} className="text-app-muted hover:text-white transition-colors">
            <FiIcons.FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-app-muted mb-2">Ticker Symbol</label>
              <input 
                required 
                autoFocus 
                type="text" 
                value={formData.code} 
                onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })} 
                placeholder="AAPL" 
                className="w-full font-mono uppercase text-lg" 
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black uppercase tracking-widest text-app-muted mb-2">Security Name</label>
              <input 
                required 
                type="text" 
                value={formData.name} 
                onChange={e => setFormData({ ...formData, name: e.target.value })} 
                placeholder="Apple Inc." 
                className="w-full text-lg" 
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-app-muted mb-2">Base Currency</label>
            <select 
              value={formData.currency} 
              onChange={e => setFormData({ ...formData, currency: e.target.value })}
              className="w-full font-bold uppercase tracking-widest text-xs"
            >
              {CURRENCIES.map(c => (
                <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-app-muted mb-2">Investment Thesis / Notes</label>
            <textarea 
              rows={3} 
              value={formData.notes} 
              onChange={e => setFormData({ ...formData, notes: e.target.value })} 
              placeholder="Why is this on your radar?" 
              className="w-full resize-none text-sm placeholder:italic" 
            />
          </div>

          <div className="pt-6 border-t border-app-border">
            <PriceLevelsEditor 
              levels={formData.priceLevels} 
              setLevels={(levels) => setFormData({ ...formData, priceLevels: levels })} 
              currency={formData.currency}
            />
          </div>
        </form>

        <div className="p-6 border-t border-app-border flex justify-end gap-3 bg-app-bg/30">
          <button type="button" onClick={onClose} className="px-6 py-3 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-all text-app-muted">Cancel</button>
          <button type="button" onClick={handleSubmit} className="px-8 py-3 rounded-lg text-xs font-black uppercase tracking-widest bg-app-accent text-black hover:bg-yellow-500 shadow-xl shadow-app-accent/10">Save Security</button>
        </div>
      </div>
    </div>
  );
}